import { Check, Minus, Search, X } from "lucide-react";
import { Fragment, startTransition, useEffect, useId, useRef, useState } from "react";

import { cn } from "../lib/cn";
import { CONTROL_FOCUS_CLASS } from "../lib/controlStyles";
import { Input } from "./FormControls";
import type { MultiSelectOption } from "./MultiSelect";
import { Popover, PopoverAnchor, PopoverContent } from "./Popover";

export interface SearchMultiSelectProps {
  label: string;
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  emptyLabel?: string;
  allLabel?: string;
  presets?: readonly { label: string; values: readonly string[] }[];
  disabled?: boolean;
  /**
   * Shows selected entries as removable chips only up to this many. A larger
   * selection reads as one line ("37 tenants selected · Clear") and is reviewed in
   * the list, where selected options come first; everything selected reads as the
   * field's "all" wording alone. Unset always shows every chip.
   */
  chipLimit?: number;
  /** The one-line summary of a selection above `chipLimit`; defaults to "N selected". */
  selectionLabel?: (count: number) => string;
  /** What the search field invites to type once something is selected; defaults to "Search <label>". */
  searchPlaceholder?: string;
}

export function SearchMultiSelect({
  label,
  options,
  value,
  onChange,
  emptyLabel = "None selected",
  allLabel,
  presets = [],
  disabled = false,
  chipLimit,
  selectionLabel = (count) => `${count} selected`,
  searchPlaceholder,
}: SearchMultiSelectProps) {
  const listId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const valueKey = JSON.stringify(value);
  const [selection, setSelection] = useState({ key: valueKey, values: value });
  if (selection.key !== valueKey) setSelection({ key: valueKey, values: value });
  const selected = selection.key === valueKey ? selection.values : value;
  const selectedValues = new Set(selected);
  const allSelected =
    Boolean(allLabel) &&
    options.length > 0 &&
    selectedValues.size === options.length &&
    options.every((option) => selectedValues.has(option.value));
  const matching = options.filter((option) =>
    `${option.label} ${option.value}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase()),
  );
  // A summarized selection is reviewed in the list: its entries come first, in the order
  // they had when the list opened, so ticking an option never makes the list jump.
  const orderBasis = useRef(selectedValues);
  if (!open) orderBasis.current = selectedValues;
  const filtered =
    chipLimit !== undefined && orderBasis.current.size > chipLimit
      ? [
          ...matching.filter((option) => orderBasis.current.has(option.value)),
          ...matching.filter((option) => !orderBasis.current.has(option.value)),
        ]
      : matching;
  const choices = [
    ...(allLabel && options.length > 0 && !query.trim()
      ? [
          {
            kind: "all" as const,
            label: `Select ${allLabel.toLocaleLowerCase()}`,
            id: `${listId}-all`,
          },
        ]
      : []),
    ...presets
      .filter((preset) =>
        `${preset.label} ${preset.values.join(" ")}`
          .toLocaleLowerCase()
          .includes(query.trim().toLocaleLowerCase()),
      )
      .map((preset, index) => ({
        ...preset,
        kind: "preset" as const,
        id: `${listId}-preset-${index}`,
      })),
    ...filtered.map((option) => ({
      ...option,
      kind: "option" as const,
      id: `${listId}-option-${option.value}`,
    })),
  ];
  const activeOption = choices[active];
  // Chips suit a few entries; beyond the limit a one-line summary replaces them.
  const summarized = chipLimit !== undefined && selected.length > chipLimit;
  const activeId = activeOption?.id;
  const panelOpen = open && !disabled;

  useEffect(() => {
    if (panelOpen && activeId) {
      document.getElementById(activeId)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [panelOpen, activeId]);

  function commit(next: string[]) {
    setSelection({ key: valueKey, values: next });
    startTransition(() => onChange(next));
  }

  function choose(option: (typeof choices)[number]) {
    commit(
      option.kind === "all"
        ? allSelected
          ? []
          : options.map((entry) => entry.value)
        : option.kind === "preset"
          ? option.values.every((entry) => selectedValues.has(entry))
            ? selected.filter((entry) => !option.values.includes(entry))
            : [...new Set([...selected, ...option.values])]
          : selected.includes(option.value)
            ? selected.filter((entry) => entry !== option.value)
            : [...selected, option.value],
    );
    setQuery("");
    setActive(-1);
    inputRef.current?.focus();
  }

  return (
    <div className="min-w-0 space-y-2">
      <Popover open={panelOpen} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted"
            />
            <Input
              ref={inputRef}
              role="combobox"
              aria-label={label}
              aria-autocomplete="list"
              aria-haspopup="listbox"
              aria-expanded={panelOpen}
              aria-controls={panelOpen ? listId : undefined}
              aria-activedescendant={panelOpen ? activeId : undefined}
              autoComplete="off"
              disabled={disabled}
              placeholder={
                allSelected
                  ? allLabel
                  : selected.length
                    ? (searchPlaceholder ?? `Search ${label.toLowerCase()}`)
                    : emptyLabel
              }
              className="pl-9"
              value={query}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(-1);
                setOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  setOpen(true);
                  if (choices.length)
                    setActive(
                      event.key === "ArrowDown"
                        ? (active + 1) % choices.length
                        : (active <= 0 ? choices.length : active) - 1,
                    );
                } else if (event.key === "Enter" && panelOpen) {
                  event.preventDefault();
                  const option = activeOption ?? choices[0];
                  if (option) choose(option);
                } else if (event.key === "Escape" && panelOpen) {
                  event.preventDefault();
                  event.stopPropagation();
                  setOpen(false);
                  setQuery("");
                } else if (event.key === "Backspace" && !query && selected.length) {
                  commit(selected.slice(0, -1));
                }
              }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          role="presentation"
          align="start"
          className="w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)] p-1.5"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onMouseDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
        >
          <div
            id={listId}
            role="listbox"
            aria-label={label}
            aria-multiselectable="true"
            className="max-h-[min(18rem,50vh)] overflow-y-auto"
          >
            {choices.map((option, index) => {
              const checked =
                option.kind === "all"
                  ? allSelected
                  : option.kind === "preset"
                    ? option.values.every((entry) => selectedValues.has(entry))
                    : selected.includes(option.value);
              const partial =
                !checked &&
                (option.kind === "all"
                  ? options.some((entry) => selectedValues.has(entry.value))
                  : option.kind === "preset" &&
                    option.values.some((entry) => selectedValues.has(entry)));
              return (
                <Fragment key={option.id}>
                  {option.kind === "option" &&
                  index > 0 &&
                  choices[index - 1]?.kind !== "option" ? (
                    <div aria-hidden className="my-1.5 h-px bg-border" />
                  ) : null}
                  <button
                    id={option.id}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    title={option.kind === "preset" ? option.values.join(", ") : undefined}
                    aria-selected={checked}
                    aria-checked={
                      option.kind === "option" ? undefined : partial ? "mixed" : checked
                    }
                    onClick={() => choose(option)}
                    className={cn(
                      "flex min-h-9 w-full items-center gap-2 rounded-control px-3 py-2 text-left text-sm text-primary hover:bg-surface-elevated",
                      active === index && "bg-surface-elevated",
                      checked && "bg-focus/10 text-interaction-text",
                    )}
                  >
                    <span className="min-w-0 flex-1 wrap-break-word">{option.label}</span>
                    {checked ? (
                      <Check aria-hidden className="size-4 shrink-0" />
                    ) : partial ? (
                      <Minus aria-hidden className="size-4 shrink-0" />
                    ) : null}
                  </button>
                </Fragment>
              );
            })}
            {!choices.length ? <p className="px-3 py-4 text-sm text-muted">No matches</p> : null}
          </div>
        </PopoverContent>
      </Popover>
      {summarized ? (
        allSelected ? null : (
          <p className="flex flex-wrap items-center gap-x-2 text-xs text-secondary">
            <span aria-live="polite">{selectionLabel(selected.length)}</span>
            <span aria-hidden className="text-muted">
              ·
            </span>
            <button
              type="button"
              disabled={disabled}
              onClick={() => commit([])}
              className={cn(
                "rounded-control font-medium text-interaction-text hover:underline disabled:opacity-50",
                CONTROL_FOCUS_CLASS,
              )}
            >
              Clear
            </button>
          </p>
        )
      ) : selected.length ? (
        <ul aria-label={`Selected ${label.toLowerCase()}`} className="flex flex-wrap gap-2">
          {selected.map((code) => {
            const name = options.find((option) => option.value === code)?.label ?? code;
            return (
              <li
                key={code}
                className="flex min-w-0 max-w-full items-center gap-1 rounded-control bg-surface-elevated py-1 pl-2 text-xs text-secondary"
              >
                <span className="min-w-0 wrap-break-word">{name}</span>
                <button
                  type="button"
                  title={`Remove ${name}`}
                  aria-label={`Remove ${name}`}
                  disabled={disabled}
                  onClick={() => commit(selected.filter((entry) => entry !== code))}
                  className={cn(
                    "touch-target grid size-6 shrink-0 place-items-center rounded-control hover:bg-surface-sunken disabled:opacity-50",
                    CONTROL_FOCUS_CLASS,
                  )}
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
